export const en = {
  // Common
  common: {
    loading: 'Loading...',
    error: 'Error',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    search: 'Search',
    back: 'Back',
    close: 'Close',
    confirm: 'Confirm',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',
    submit: 'Submit',
    reset: 'Reset',
    clear: 'Clear',
    view: 'View',
    details: 'Details',
    settings: 'Settings',
    logout: 'Logout',
    login: 'Login',
    signup: 'Sign Up',
    profile: 'Profile',
    collection: 'Collection',
    insights: 'Insights',
    overview: 'Overview',
    categories: 'Categories',
    preorders: 'Preorders',
    recent: 'Recent',
    total: 'Total',
    average: 'Average',
    price: 'Price',
    quantity: 'Quantity',
    author: 'Author',
    publisher: 'Publisher',
    isbn: 'ISBN',
    genre: 'Genre',
    type: 'Type',
    language: 'Language',
    source: 'Source',
    date: 'Date',
    status: 'Status',
    value: 'Value',
    items: 'Items',
    owned: 'Owned',
    preorder: 'Preorder',
    created: 'Created',
    updated: 'Updated',
    title: 'Title',
    description: 'Description',
    image: 'Image',
    currency: 'Currency',
    preferences: 'Preferences',
    clone: 'Clone',
    cloneSuccess: 'Book cloned successfully',
    cloneError: 'Failed to clone book',
    code: 'Code'
  },

  // Navigation
  nav: {
    home: 'Home',
    collection: 'Collection',
    insights: 'Insights',
    wishlist: 'Wishlist',
    settings: 'Settings',
    profile: 'Profile',
    logout: 'Logout',
    language: 'Language'
  },

  // Landing Page
  landing: {
    title: 'BookPort',
    subtitle: 'Your Personal Book Collection Manager',
    description: 'Organize, track, and manage your book collection with ease. From novels to textbooks, keep everything in one beautiful, organized place.',
    getStarted: 'Get Started',
    signUp: 'Sign Up',
    goToCollection: 'Go to Collection',
    sampleCollection: 'Sample Collection',
    features: {
      title: 'Powerful Features',
      subtitle: 'Everything you need to manage your book collection',
      completeManagement: {
        title: 'Complete Book Management',
        description: 'Organize your entire book collection with detailed information including author, publisher, genre, and more.'
      },
      smartSearch: {
        title: 'Smart Search',
        description: 'Find any book in your collection instantly with powerful search by title, author, ISBN, or publisher.'
      },
      multiCurrency: {
        title: 'Multi-Currency Support',
        description: 'Track book values in your preferred currency with automatic exchange rate updates.'
      },
      bookTracking: {
        title: 'Book Tracking',
        description: 'Keep track of your reading progress, book conditions, and collection growth over time.'
      },
      cloudSync: {
        title: 'Cloud Sync',
        description: 'Access your book collection from anywhere with secure cloud synchronization.'
      },
      secure: {
        title: 'Secure & Private',
        description: 'Your book collection data is private and secure with enterprise-grade encryption.'
      }
    },
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Get started in just a few simple steps',
      signUp: {
        title: 'Sign Up',
        description: 'Create your free account in seconds'
      },
      addBooks: {
        title: 'Add Books',
        description: 'Add your books with detailed information'
      },
      organize: {
        title: 'Organize',
        description: 'Organize by genre, author, or any criteria'
      },
      monitor: {
        title: 'Monitor',
        description: 'Monitor your collection growth and value'
      }
    },
    benefits: {
      title: 'Why Choose BookPort?',
      subtitle: 'Join thousands of book lovers who trust BookPort to manage their collections',
      neverLose: 'Never lose track of your books again',
      trackValue: 'Track the value of your collection',
      planPurchases: 'Plan your next book purchases',
      shareCollection: 'Share your collection with friends',
      mobileFriendly: 'Access from any device',
      freeToUse: 'Completely free to use'
    },
    readerTypes: {
      title: 'Perfect for All Readers',
      description: 'Whether you collect novels, textbooks, comics, or rare books, BookPort adapts to your collection style.',
      startCollection: 'Start Your Collection'
    },
    cta: {
      title: 'Ready to Organize Your Books?',
      subtitle: 'Join thousands of book lovers who have already organized their collections with BookPort.',
      createAccount: 'Create Account'
    }
  },

  // Collection
  collection: {
    title: 'My Book Collection',
    addNewBook: 'Add New Book',
    addBook: 'Add Book',
    searchPlaceholder: 'Search by title, author, ISBN, or publisher...',
    noBooks: 'No books in your collection yet.',
    noSearchResults: 'No books found matching your search.',
    addFirstBook: 'Add Your First Book',
    startBuildingCollection: 'Start building your collection by adding your first book!',
    tryAdjustingSearch: 'Try adjusting your search terms.',
    showingResults: 'Showing {count} of {total} books',
    endOfResults: 'You\'ve reached the end of your collection',
    summary: {
      title: 'Track and organize your precious book collection',
      totalBooks: 'Total Books',
      foundBooks: 'Found Books',
      totalValue: 'Total Value',
      averagePrice: 'Average Price',
      totalQuantity: 'Total Quantity'
    }
  },

  // Book Detail
  bookDetail: {
    newBook: 'Add New Book',
    editBook: 'Edit Book',
    title: 'Title',
    author: 'Author',
    isbn: 'ISBN',
    publisher: 'Publisher',
    series: 'Series',
    genre: 'Genre',
    type: 'Type',
    language: 'Language',
    price: 'Price',
    quantity: 'Quantity',
    source: 'Source',
    imageUrl: 'Image URL',
    createdDate: 'Created Date',
    notes: 'Notes',
    save: 'Save Book',
    cancel: 'Cancel',
    delete: 'Delete Book',
    deleteConfirm: 'Are you sure you want to delete this book?',
    deleteConfirmDescription: 'This action cannot be undone.',
    imagePlaceholder: 'No Image'
  },

  // Insights
  insights: {
    title: 'Collection Insights',
    subtitle: 'Detailed analytics and insights about your book collection',
    overview: {
      title: 'Overview',
      collectionStatus: 'Collection Status',
      ownedItems: 'Owned Items',
      preorderItems: 'Preorder Items',
      valueBreakdown: 'Value Breakdown',
      ownedValue: 'Owned Value',
      preorderValue: 'Preorder Value',
      mostValuableItems: 'Most Valuable Items'
    },
    categories: {
      title: 'Categories',
      topPublishers: 'Top Publishers',
      topGenres: 'Top Genres',
      topTypes: 'Top Types',
      topLanguages: 'Top Languages',
      topSources: 'Top Sources'
    },
    preorders: {
      title: 'Preorders',
      preorderSummary: 'Preorder Summary',
      items: 'Items',
      totalValue: 'Total Value'
    },
    recent: {
      title: 'Recent',
      recentAdditions: 'Recent Additions'
    },
    stats: {
      totalItems: 'Total Items',
      totalValue: 'Total Value',
      totalQuantity: 'Total Quantity',
      averagePrice: 'Average Price'
    }
  },

  // Settings
  settings: {
    title: 'User Settings',
    currency: 'Currency',
    language: 'Language',
    saveSettings: 'Save Settings',
    settingsSaved: 'Settings saved successfully!',
    settingsError: 'Failed to save settings',
    currencyDescription: 'Set your preferred currency for displaying prices throughout the application. All prices will be converted to your selected currency.',
    languageDescription: 'Choose your preferred language for the application interface. The language will be applied immediately.',
    preferences: 'Preferences'
  },

  // Auth
  auth: {
    login: {
      title: 'Sign In to BookPort',
      email: 'Email',
      password: 'Password',
      signIn: 'Sign In',
      signInWithGoogle: 'Sign in with Google',
      noAccount: "Don't have an account?",
      signUp: 'Sign Up',
      forgotPassword: 'Forgot password?',
      loginError: 'Failed to log in'
    },
    signup: {
      title: 'Create Your Account',
      displayName: 'Display Name',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      signUp: 'Sign Up',
      signUpWithGoogle: 'Sign up with Google',
      haveAccount: 'Already have an account?',
      signIn: 'Sign In',
      signupError: 'Failed to create an account',
      passwordMismatch: 'Passwords do not match'
    }
  },

  // Languages
  languages: {
    en: 'English',
    th: 'ไทย',
    ja: '日本語',
    zh: '中文'
  },

  // Wishlist
  wishlist: {
    title: 'Wishlist',
    subtitle: 'Track books you want to add to your collection',
    addNewWish: 'Add Wish Item',
    addFirstWish: 'Add Your First Wish Item',
    searchPlaceholder: 'Search wishlist items...',
    noWishItems: 'No items in your wishlist yet.',
    noSearchResults: 'No wishlist items found matching your search.',
    showingResults: 'Showing {count} of {total} items',
    convertToBook: 'Convert to Book',
    convert: 'Yes, I got it!',
    converting: 'Converting...',
    convertConfirm: 'Did you get it?',
    convertConfirmDescription: 'This will move this item from your wishlist to your collection. You can then add quantity and source information.',
    deleteConfirm: 'Delete Wish Item?',
    deleteConfirmDescription: 'This will permanently delete this wish item. This action cannot be undone.',
    // New celebratory conversion messages
    congratulations: '🎉 Congratulations! 🎉',
    finallyGotIt: 'You finally got your hands on it!',
    thatsAwesome: "That's awesome! 🎊 Time to move this beauty from your wishlist to your collection. You can add details like quantity and where you got it from.",
    readyToCelebrate: 'Ready to celebrate this addition to your collection?',
    maybeLater: 'Maybe Later',
    yesAddToCollection: '🎉 Yes, Add to Collection!',
    addingToCollection: 'Adding to Collection...'
  },

  // Currencies
  currencies: {
    USD: 'US Dollar',
    THB: 'Thai Baht',
    JPY: 'Japanese Yen',
    CNY: 'Chinese Yuan'
  },


}; 